import { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STAGES = [
  { word: 'Lost', desc: 'You arrive carrying years of everyone else' },
  { word: 'Overwhelmed', desc: 'We name what has been too heavy for too long' },
  { word: 'Awakening', desc: 'The first quiet moments of hearing yourself again' },
  { word: 'Presence', desc: 'You learn to stay. With your breath. With your life' },
  { word: 'Connection', desc: 'You return to others — without abandoning yourself' },
  { word: 'Rise', desc: 'Not louder. Not faster. Simply, fully you.' },
];

const Leaf = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2C7 7 4 12 4 16c0 3.3 2.7 6 6 6 4 0 9-3 12-8-2-1-5-2-8-1 2-2 5-3 8-3-1-4-5-8-10-8z" />
  </svg>
);

const Flower = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <circle cx="12" cy="12" r="2.4" />
    <ellipse cx="12" cy="6.5" rx="2.2" ry="3.4" opacity="0.75" />
    <ellipse cx="12" cy="17.5" rx="2.2" ry="3.4" opacity="0.75" />
    <ellipse cx="6.5" cy="12" rx="3.4" ry="2.2" opacity="0.75" />
    <ellipse cx="17.5" cy="12" rx="3.4" ry="2.2" opacity="0.75" />
  </svg>
);

const Journey = () => {
  const root = useRef(null);
  const track = useRef(null);
  const vine = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add('(min-width: 768px)', () => {
        const len = vine.current.getTotalLength();
        gsap.set(vine.current, { strokeDasharray: len, strokeDashoffset: len });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: root.current,
            start: 'top top',
            end: `+=${STAGES.length * 65}%`,
            pin: true,
            scrub: 1.4,
          },
        });
        tl.to(track.current, { x: () => -(track.current.scrollWidth - window.innerWidth), ease: 'none', duration: 10 }, 0);
        tl.to(vine.current, { strokeDashoffset: 0, ease: 'none', duration: 10 }, 0);

        gsap.utils.toArray('.journey-stage').forEach((stage, i) => {
          const p = (i / (STAGES.length - 1)) * 10;
          tl.fromTo(stage.querySelector('.stage-word'), { opacity: 0.25, scale: 0.92 }, { opacity: 1, scale: 1, duration: 1.4, ease: 'power2.out' }, Math.max(0, p - 0.9));
          tl.fromTo(stage.querySelector('.stage-desc'), { opacity: 0, y: 20 }, { opacity: 0.7, y: 0, duration: 1.2, ease: 'power2.out' }, Math.max(0, p - 0.6));
          tl.fromTo(stage.querySelectorAll('.stage-bloom'), { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 1, stagger: 0.25, ease: 'power2.out' }, Math.max(0, p - 0.4));
        });
      });
      mm.add('(max-width: 767px)', () => {
        gsap.utils.toArray('.journey-stage').forEach((stage) => {
          gsap.fromTo(stage, { opacity: 0, y: 50 }, {
            opacity: 1, y: 0, duration: 1.4, ease: 'power2.out',
            scrollTrigger: { trigger: stage, start: 'top 75%' },
          });
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} data-testid="journey-section" className="relative overflow-hidden bg-[#FAFAF8] md:h-screen">
      <div className="absolute top-16 md:top-24 left-6 md:left-16 z-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#B85C38] mb-4">The Program Journey</p>
        <h2 className="font-serif-display font-light text-3xl md:text-5xl text-[#2C2C2A]">Six seasons of <em className="text-[#1A4D38]">returning.</em></h2>
      </div>

      <div ref={track} className="flex flex-col md:flex-row md:h-full md:w-max will-change-transform pt-48 md:pt-0 pb-20 md:pb-0">
        <svg
          className="hidden md:block absolute left-0 top-[62%] pointer-events-none"
          width={STAGES.length * 640}
          height="120"
          viewBox={`0 0 ${STAGES.length * 640} 120`}
          fill="none"
        >
          <path
            ref={vine}
            d={`M 40 60 ${STAGES.map((_, i) => `Q ${i * 640 + 320} ${i % 2 === 0 ? 10 : 110}, ${(i + 1) * 640} 60`).join(' ')}`}
            stroke="#7A9E86"
            strokeWidth="1.5"
          />
        </svg>

        {STAGES.map((s, i) => (
          <div
            key={s.word}
            data-testid={`journey-stage-${s.word.toLowerCase()}`}
            className="journey-stage relative w-full md:w-[640px] md:h-full flex-shrink-0 flex flex-col items-center justify-center text-center px-10 py-14 md:py-0"
          >
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#7A9E86] mb-6">Stage {i + 1}</p>
            <h3 className="stage-word font-serif-display font-light text-5xl md:text-7xl text-[#2C2C2A] mb-6">{s.word}</h3>
            <p className="stage-desc text-sm md:text-base font-light text-[#2C2C2A]/70 max-w-xs leading-[1.9]">{s.desc}</p>
            <div className="hidden md:flex gap-4 mt-12 items-end">
              <Leaf className="stage-bloom w-4 h-4 text-[#7A9E86]" />
              {i >= 2 && <Leaf className="stage-bloom w-5 h-5 text-[#2D6B4A]" />}
              {i >= 4 && <Flower className="stage-bloom w-5 h-5 text-[#B85C38]" />}
              {i === 5 && <Flower className="stage-bloom w-6 h-6 text-[#C98B3A]" />}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Journey;
